import { useQuery } from "@tanstack/react-query";
import { SimpleGrid, Grid, GridItem, HStack, VStack, Text, Skeleton, Box } from "@chakra-ui/react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import { PageHeader, SectionCard, StatCard, IconTile, EmptyState, CurrencyText } from "../../../shared/ui";
import { TrendingUp, Clock, Calendar, Wallet, BarChart3 } from "../../../shared/icons";
import { formatBRL } from "../../../shared/lib/currency";
import { kpisApi } from "../api/kpisApi";
import { useChartColors } from "../../../shared/theme/useChartColors";

function tooltipFormatter(value) {
  return [formatBRL(value), "Faturamento"];
}

export default function KpisPage() {
  const { data: kpis, isLoading } = useQuery({ queryKey: ["kpis"], queryFn: kpisApi.get });
  const chart = useChartColors();

  const serie = kpis?.serie_historica_6_meses ?? [];
  const participacao = kpis?.participacao_por_vinculo ?? [];
  const maxParticipacao = participacao[0]?.faturamento || 1;

  return (
    <>
      <PageHeader title="Inteligência financeira" description={kpis?.periodo?.rotulo} />

      <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={4} mb={6}>
        <SectionCard p={0}>
          <StatCard label="Faturamento do mês" value={kpis?.faturamento ?? 0} icon={Wallet} tone="brand" isLoading={isLoading} />
        </SectionCard>
        <SectionCard p={0}>
          <StatCard label="Valor médio por hora" value={kpis?.valor_medio_por_hora ?? 0} icon={Clock} tone="teal" isLoading={isLoading} />
        </SectionCard>
        <SectionCard p={4}>
          <HStack spacing={3} align="flex-start">
            <IconTile icon={Calendar} tone="warning" />
            <VStack align="flex-start" spacing={0}>
              <Text color="app-muted-fg" fontSize="sm">
                Plantões no mês
              </Text>
              {isLoading ? <Skeleton height="28px" width="60px" mt={1} /> : <Text fontSize="2xl" fontWeight="bold">{kpis?.plantoes_no_mes ?? 0}</Text>}
            </VStack>
          </HStack>
        </SectionCard>
        <SectionCard p={0}>
          <StatCard label="Ticket médio/plantão" value={kpis?.ticket_medio ?? 0} icon={TrendingUp} tone="success" isLoading={isLoading} />
        </SectionCard>
      </SimpleGrid>

      {kpis?.insight ? (
        <SectionCard mb={6}>
          <HStack spacing={3}>
            <IconTile icon={BarChart3} tone="brand" />
            <VStack align="flex-start" spacing={0}>
              <Text fontSize="xs" color="app-muted-fg" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider">
                Insight do mês
              </Text>
              <Text fontWeight="medium">{kpis.insight}</Text>
            </VStack>
          </HStack>
        </SectionCard>
      ) : null}

      <Grid templateColumns={{ base: "1fr", lg: "3fr 2fr" }} gap={5}>
        {/* minW={0}: sem isso a trilha do grid não encolhe abaixo da largura já medida pelo
            ResponsiveContainer, e o gráfico estoura a tela ao girar/estreitar o celular. */}
        <GridItem minW={0}>
          <SectionCard title="Faturamento — últimos 6 meses">
            {isLoading ? (
              <Skeleton height="260px" />
            ) : serie.length === 0 ? (
              <EmptyState title="Sem dados ainda" description="Registre plantões para ver a evolução do faturamento." />
            ) : (
              <Box height="260px">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={serie} margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={chart.grid} />
                    <XAxis dataKey="rotulo" tickLine={false} axisLine={false} fontSize={12} tick={{ fill: chart.axis }} />
                    <YAxis tickLine={false} axisLine={false} fontSize={11} tick={{ fill: chart.axis }} tickFormatter={(v) => `${Math.round(v / 1000)}k`} width={36} />
                    <Tooltip
                      formatter={tooltipFormatter}
                      cursor={{ fill: chart.cursor }}
                      contentStyle={{ background: chart.surface, borderColor: chart.border }}
                      labelStyle={{ color: chart.text }}
                      itemStyle={{ color: chart.barText }}
                    />
                    <Bar dataKey="faturamento" fill={chart.bar} radius={[6, 6, 0, 0]} maxBarSize={44} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            )}
          </SectionCard>
        </GridItem>
        <GridItem>
          <SectionCard title="Faturamento por vínculo">
            {isLoading ? (
              <Skeleton height="260px" />
            ) : participacao.length === 0 ? (
              <EmptyState title="Sem vínculos" description="Cadastre vínculos e registre plantões para comparar." />
            ) : (
              <VStack align="stretch" spacing={3}>
                {participacao.map((p) => (
                  <Box key={p.vinculo_id}>
                    <HStack justify="space-between" mb={1}>
                      <Text fontSize="sm">{p.nome}</Text>
                      <Text fontSize="sm" fontWeight="semibold">
                        <CurrencyText value={p.faturamento} />
                      </Text>
                    </HStack>
                    <Box bg="app-muted" borderRadius="full" h="6px">
                      <Box bg="teal.400" borderRadius="full" h="100%" w={`${Math.max(4, (p.faturamento / maxParticipacao) * 100)}%`} />
                    </Box>
                  </Box>
                ))}
              </VStack>
            )}
          </SectionCard>
        </GridItem>
      </Grid>
    </>
  );
}
